import React, { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api, { API_BASE } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, QrCode, AlertCircle, Receipt, Users, Megaphone, Car, Bike, Sparkles, KeyRound, AlertTriangle, Camera, X } from "lucide-react";
import VisitorPhotoModal, { getVisitorPhotoSrc } from "@/components/VisitorPhotoModal";
import ResidentRecentActivity from "@/components/ResidentRecentActivity";
import { toast } from "sonner";

function Stat({ label, value, hint, testid }) {
    return (
        <div className="nest-card p-4 sm:p-6" data-testid={testid}>
            <p className="nest-stat-label text-[10px] sm:text-[11px] truncate">{label}</p>
            <p className="font-display text-2xl sm:text-4xl mt-1.5 sm:mt-3 tracking-tighter">{value}</p>
            {hint && <p className="text-[11px] sm:text-xs text-nest-ink3 mt-1 sm:mt-2 truncate">{hint}</p>}
        </div>
    );
}

function formatTimeSafe(dateStr) {
    if (!dateStr) return "";
    try {
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return "";
        return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    } catch {
        return "";
    }
}

function ResidentDash({ user, stats }) {
    const navigate = useNavigate();
    const [pending, setPending] = useState([]);
    const [parkingAlerts, setParkingAlerts] = useState([]);
    const [alertDismissed, setAlertDismissed] = useState(false);
    const [activePhotoVisitor, setActivePhotoVisitor] = useState(null);
    const loadPending = () => {
        api.get("/walkins/pending/me")
            .then((r) => setPending(r.data || []))
            .catch(() => {});
        api.get("/resident/parking-alerts")
            .then((r) => setParkingAlerts(r.data || []))
            .catch(() => {});
    };

    useEffect(() => {
        loadPending();
        const iv = setInterval(() => {
            if (typeof document !== "undefined" && document.visibilityState === "visible") {
                loadPending();
            }
        }, 6000);
        return () => clearInterval(iv);
    }, []);

    const decide = async (w, decision) => {
        try {
            await api.post(`/walkins/${w.walkin_id}/decision`, { decision });
            setPending((arr) => arr.filter((x) => x.walkin_id !== w.walkin_id));
            toast.success(decision === "approved" ? `✓ Approved entry for ${w.name}` : `✕ Denied entry for ${w.name}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Action failed");
        }
    };
    return (
        <div className="grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-4 sm:gap-6">
            {parkingAlerts.length > 0 && !alertDismissed && (
                <div className="md:col-span-8 lg:col-span-12" data-testid="parking-alerts-compact-banner">
                    <div className="rounded-xl border border-rose-300 bg-rose-50 px-3.5 py-2.5 flex items-center justify-between gap-3 shadow-2xs">
                        <div className="flex items-center gap-2.5 min-w-0">
                            <AlertTriangle size={18} className="text-rose-600 shrink-0" />
                            <p className="text-xs font-semibold text-rose-950 truncate">
                                <span className="font-bold text-rose-700">Wing {user?.tower || "C"} Parking Alert:</span>{" "}
                                {parkingAlerts.length === 1
                                    ? `Unauthorized vehicle ${parkingAlerts[0].vehicle_no} reported.`
                                    : `${parkingAlerts.length} unauthorized vehicles reported.`}
                            </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <button
                                type="button"
                                onClick={() => navigate("/notices")}
                                className="text-xs font-bold text-rose-700 hover:text-rose-900 underline cursor-pointer"
                            >
                                View Notice
                            </button>
                            <button
                                type="button"
                                onClick={() => setAlertDismissed(true)}
                                className="p-1 rounded-lg text-rose-500 hover:text-rose-700 hover:bg-rose-100/60 cursor-pointer"
                                aria-label="Dismiss"
                            >
                                <X size={15} />
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {pending.length > 0 && (
                <div className="md:col-span-8 lg:col-span-12 rounded-2xl border border-nest-warn bg-white p-4 sm:p-6 shadow-xs" data-testid="pending-walkins-banner">
                    <div className="flex items-center justify-between">
                        <div>
                            <span className="text-[10px] font-bold uppercase tracking-widest text-nest-warn">Awaiting your approval</span>
                            <h3 className="font-display text-xl sm:text-2xl mt-0.5 tracking-tight">{pending.length} visitor{pending.length > 1 ? "s" : ""} at your gate</h3>
                        </div>
                    </div>
                    <div className="mt-3 sm:mt-4 grid gap-2.5 sm:gap-3">
                        {pending.map((w) => (
                            <div key={w.walkin_id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 border border-nest-line rounded-xl hover:border-nest-clay transition" data-testid={`pending-walkin-${w.walkin_id}`}>
                                <div className="flex items-center gap-3 min-w-0">
                                    <img
                                        src={getVisitorPhotoSrc(w)}
                                        alt=""
                                        onClick={() => setActivePhotoVisitor(w)}
                                        className="h-14 w-14 sm:h-16 sm:w-16 rounded-lg object-cover bg-nest-alt cursor-pointer hover:ring-2 hover:ring-nest-clay shrink-0 transition"
                                        title="Click to view full visitor photo"
                                    />
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                            <p className="font-medium text-sm sm:text-base truncate">{w.name}</p>
                                            {w.vehicle_no && (
                                                <span className="inline-flex items-center gap-1 font-mono text-[10px] bg-nest-alt px-1.5 py-0.5 rounded border border-nest-line text-nest-ink">
                                                    {w.vehicle_type === "bike" ? <Bike size={11} className="text-nest-clay" /> : <Car size={11} className="text-nest-clay" />}
                                                    <span>{w.vehicle_no}</span>
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-xs text-nest-ink3 truncate mt-0.5">{w.purpose}{w.created_at ? ` · ${formatTimeSafe(w.created_at)}` : ""}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 w-full sm:w-auto shrink-0 pt-2 sm:pt-0 border-t border-nest-line sm:border-0">
                                    <button onClick={() => navigate(`/walkin/${w.walkin_id}`)} className="flex-1 sm:flex-none text-xs px-3 py-2 rounded-lg border border-nest-line hover:border-nest-ink text-center transition" data-testid={`walkin-view-${w.walkin_id}`}>View</button>
                                    <button onClick={() => decide(w, "approved")} className="flex-1 sm:flex-none text-xs px-3 py-2 rounded-lg bg-nest-sage text-white text-center font-medium shadow-xs transition" data-testid={`walkin-quickapprove-${w.walkin_id}`}>Approve</button>
                                    <button onClick={() => decide(w, "rejected")} className="flex-1 sm:flex-none text-xs px-3 py-2 rounded-lg border border-nest-err text-nest-err text-center font-medium transition" data-testid={`walkin-quickreject-${w.walkin_id}`}>Reject</button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            <VisitorPhotoModal visitor={activePhotoVisitor} onClose={() => setActivePhotoVisitor(null)} />
            <div className="lg:col-span-12 xl:col-span-7 nest-card p-5 sm:p-7 flex flex-col justify-between" style={{ minHeight: 220 }}>
                <div>
                    <p className="nest-stat-label">Resident · {user?.tower}-{user?.flat_no}</p>
                    <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tighter mt-2 sm:mt-3">Hi {user?.name?.split(" ")[0]}, your nest is in order.</h1>
                    <p className="text-nest-ink2 text-xs sm:text-sm lg:text-base mt-2 sm:mt-3 max-w-xl">Pre-approve a guest in seconds, check pending dues or raise a complaint — all from one calm canvas.</p>
                </div>
                <div className="flex flex-col sm:flex-row flex-wrap gap-2.5 sm:gap-3 mt-5 sm:mt-6">
                    <button onClick={() => navigate("/visitors")} className="nest-btn-primary flex items-center justify-center gap-2 text-sm" data-testid="cta-new-visitor"><QrCode size={16}/> New visitor pass</button>
                    <button onClick={() => navigate("/complaints")} className="px-5 py-3 rounded-xl border border-nest-line hover:border-nest-ink font-medium text-sm text-center" data-testid="cta-raise-complaint">Raise a complaint</button>
                </div>
            </div>
            <div className="lg:col-span-12 xl:col-span-5 grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-2 gap-2.5 sm:gap-3.5">
                <Stat label="Notices" value={stats?.notices_count ?? 0} hint="From admin" testid="stat-notices" />
                <Stat label="Open complaints" value={stats?.open_complaints ?? 0} hint="Society-wide" testid="stat-open-complaints" />
                <Stat label="Residents" value={stats?.total_residents ?? 0} hint="In directory" testid="stat-residents" />
                <Stat label="Visitors today" value={stats?.visitors_today ?? 0} hint="At the gate" testid="stat-visitors-today" />
            </div>
            <div className="md:col-span-8 lg:col-span-12">
                <ResidentRecentActivity user={user} onPhotoClick={setActivePhotoVisitor} />
            </div>
            <div className="md:col-span-8 lg:col-span-12 nest-card p-5 sm:p-8">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="nest-stat-label">Quick paths</p>
                        <h3 className="font-display text-xl sm:text-2xl mt-1 sm:mt-2 tracking-tight">What would you like to do?</h3>
                    </div>
                </div>
                <div className="mt-4 sm:mt-6 grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-2.5 sm:gap-4">
                    {[
                        { to: "/visitors", label: "Visitor passes", icon: QrCode },
                        { to: "/bills", label: "Maintenance bills", icon: Receipt },
                        { to: "/notices", label: "Society notices", icon: Megaphone },
                        { to: "/directory", label: "Neighbour directory", icon: Users },
                    ].map((q) => (
                        <button key={q.to} onClick={() => navigate(q.to)} className="text-left p-3.5 sm:p-5 rounded-xl border border-nest-line hover:border-nest-clay hover:bg-white transition-all group" data-testid={`quick-${q.to.replace("/", "")}`}>
                            <q.icon className="text-nest-clay" size={20}/>
                            <p className="mt-2.5 sm:mt-3 font-display text-base sm:text-lg leading-snug">{q.label}</p>
                            <ArrowUpRight className="mt-1 sm:mt-2 text-nest-ink3 group-hover:text-nest-ink" size={16}/>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}

function AdminDash({ user, stats }) {
    const navigate = useNavigate();
    const pendingCount = stats?.pending_residents ?? 0;

    return (
        <div>
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-2 sm:gap-4">
                <div>
                    <p className="nest-stat-label">Admin control room</p>
                    <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tighter mt-1 sm:mt-2">Society at a glance.</h1>
                    <p className="text-nest-ink2 text-xs sm:text-sm lg:text-base mt-1 sm:mt-1.5">Operational truth for {user?.name?.split(" ")[0]}'s nest, refreshed live.</p>
                </div>
                <div className="flex items-center gap-2.5 flex-wrap">
                    {(user?.email || "").toLowerCase() === "aws.tilak007@gmail.com" && (
                        <button
                            onClick={() => navigate("/directory?tab=test-admins")}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900 text-xs font-semibold transition cursor-pointer shadow-2xs"
                            title="Manage Test Admin accounts (Super Admin only)"
                            data-testid="dashboard-manage-test-admins-btn"
                        >
                            <KeyRound size={13} className="text-amber-700" />
                            <span>Manage Test Admins</span>
                        </button>
                    )}
                    <p className="text-[10px] sm:text-xs text-nest-ink3 tracking-widest uppercase">Last sync · just now</p>
                </div>
            </div>

            {pendingCount > 0 && (
                <div className="mt-4 sm:mt-6 p-4 rounded-2xl border border-amber-300 bg-amber-50/90 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 animate-fade-in" data-testid="pending-residents-banner">
                    <div className="flex items-center gap-3">
                        <span className="h-3 w-3 rounded-full bg-amber-500 animate-pulse shrink-0"></span>
                        <div>
                            <p className="font-semibold text-amber-950 text-sm">
                                {pendingCount} Resident Profile{pendingCount > 1 ? "s" : ""} Awaiting Your Verification
                            </p>
                            <p className="text-xs text-amber-800 mt-0.5">
                                Verify flat numbers and authorize access to the society dashboard.
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={() => navigate("/directory")}
                        className="px-4 py-2 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-semibold text-xs whitespace-nowrap transition cursor-pointer self-start sm:self-auto w-full sm:w-auto text-center"
                    >
                        Review Profiles →
                    </button>
                </div>
            )}

            <div className="mt-5 sm:mt-7 grid grid-cols-2 md:grid-cols-4 gap-2.5 sm:gap-4 lg:gap-5">
                <Stat label="Visitors today" value={stats?.visitors_today ?? 0} testid="stat-visitors-today" />
                <Stat label="Open complaints" value={stats?.open_complaints ?? 0} testid="stat-open-complaints" />
                <Stat label="Unpaid bills" value={stats?.unpaid_bills ?? 0} testid="stat-unpaid-bills" />
                <Stat label="Residents" value={stats?.total_residents ?? 0} testid="stat-residents" />
            </div>
            <div className="mt-6 sm:mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3.5 sm:gap-6">
                <a href="/notices" className="nest-card p-4 sm:p-6 block hover:border-nest-clay transition" data-testid="admin-card-notices">
                    <Megaphone className="text-nest-clay" size={22} />
                    <p className="font-display text-xl sm:text-2xl mt-3 sm:mt-4 tracking-tight">Post a notice</p>
                    <p className="text-xs sm:text-sm text-nest-ink2 mt-1 sm:mt-2">Broadcast to all residents instantly.</p>
                </a>
                <a href="/bills" className="nest-card p-4 sm:p-6 block hover:border-nest-clay transition" data-testid="admin-card-bills">
                    <Receipt className="text-nest-clay" size={22} />
                    <p className="font-display text-xl sm:text-2xl mt-3 sm:mt-4 tracking-tight">Generate bills</p>
                    <p className="text-xs sm:text-sm text-nest-ink2 mt-1 sm:mt-2">Track maintenance dues by flat.</p>
                </a>
                <a href="/complaints" className="nest-card p-4 sm:p-6 block hover:border-nest-clay transition" data-testid="admin-card-complaints">
                    <AlertCircle className="text-nest-clay" size={22} />
                    <p className="font-display text-xl sm:text-2xl mt-3 sm:mt-4 tracking-tight">Resolve complaints</p>
                    <p className="text-xs sm:text-sm text-nest-ink2 mt-1 sm:mt-2">Stay ahead of resident issues.</p>
                </a>
            </div>
        </div>
    );
}

export default function Dashboard() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);

    useEffect(() => {
        if (user?.role === "guard") {
            navigate("/guard", { replace: true });
            return;
        }
        api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    }, [user, navigate]);

    if (!user) return null;

    return (
        <div className="animate-fade-up" data-testid="dashboard-root">
            {user.role === "admin" ? <AdminDash user={user} stats={stats} /> : <ResidentDash user={user} stats={stats} />}
        </div>
    );
}
